import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, FileDown, Copy, ChevronLeft, ChevronRight, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import {
  TableColumnVisibilityMenu,
  initialColumnVisibility,
  countVisibleInRange,
  appendSelectedColumnsParam,
} from '@/components/shared/TableColumnVisibilityMenu'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { inventoryMovementService } from '@/services/inventoryMovementService'
import { stockReportService } from '@/services/stockReportService'
import type { ItemEntity, Warehouse } from '@/api/types'
import type { StockReportRow } from '@/modules/inventory/reports/types'

const SELECT_EMPTY = '__stock_report_empty__'
const SELECT_ALL = '0'

function isEmptySelection(v: string) {
  return v === '' || v === SELECT_EMPTY
}

function isAllSelection(v: string) {
  return v === SELECT_ALL
}

function formatDateForInput(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDisplayDate(dateString?: string) {
  if (!dateString) return '-'
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

function formatQty(n: number) {
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function formatMoney(n: number) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function escapeCsvCell(v: string) {
  if (v.includes('"') || v.includes(',') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`
  }
  return v
}

const STOCK_COLUMN_ORDER = [
  'itemCode',
  'name',
  'category',
  'subCategory',
  'uom',
  'type',
  'warehouse',
  'batchCode',
  'availableQuantity',
  'damagedStock',
  'expiredStock',
  'holdStock',
  'totalQuantity',
  'unitSalesPrice',
  'expiryDate',
] as const

export const StockReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [warehousesLoading, setWarehousesLoading] = useState(false)
  const [items, setItems] = useState<ItemEntity[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)

  const [warehouseId, setWarehouseId] = useState(SELECT_EMPTY)
  const [itemId, setItemId] = useState(SELECT_EMPTY)
  const [reportDate, setReportDate] = useState('')

  const [rows, setRows] = useState<StockReportRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [colVis, setColVis] = useState<Record<string, boolean>>(() =>
    initialColumnVisibility([...STOCK_COLUMN_ORDER]),
  )

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)

  const prevBranchIdRef = useRef(0)

  useEffect(() => {
    const today = new Date()
    setReportDate(formatDateForInput(today))
  }, [])

  useEffect(() => {
    if (!selectedBranchId) {
      setWarehouses([])
      prevBranchIdRef.current = 0
      return
    }
    if (prevBranchIdRef.current !== 0 && prevBranchIdRef.current !== selectedBranchId) {
      setWarehouseId(SELECT_EMPTY)
      setItemId(SELECT_EMPTY)
      setRows([])
      setTotalCount(0)
      setHasSearched(false)
    }
    prevBranchIdRef.current = selectedBranchId

    let cancelled = false
    setWarehousesLoading(true)
    void inventoryMovementService
      .getWarehousesByBranchId(selectedBranchId)
      .then((list) => {
        if (!cancelled) setWarehouses(list)
      })
      .catch((e: { response?: { data?: { message?: string } } }) => {
        if (!cancelled) {
          setWarehouses([])
          toast.error(e?.response?.data?.message || t('unsellableInventory.warehousesLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setWarehousesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedBranchId, t])

  useEffect(() => {
    if (isEmptySelection(warehouseId) || isAllSelection(warehouseId)) {
      setItems([])
      setItemsLoading(false)
      return
    }
    const whNum = Number(warehouseId)
    if (!whNum) {
      setItems([])
      return
    }
    let cancelled = false
    setItemsLoading(true)
    void inventoryMovementService
      .getItemsByWarehouse(whNum)
      .then((list) => {
        if (!cancelled) setItems(list)
      })
      .catch((e: { response?: { data?: { message?: string } } }) => {
        if (!cancelled) {
          setItems([])
          toast.error(e?.response?.data?.message || t('unsellableInventory.itemsLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [warehouseId, t])

  const itemOptions = useMemo(() => {
    return items.map((it) => {
      const name =
        language === 'ar' ? it.itemNameAr || it.itemNameEn || it.itemCode : it.itemNameEn || it.itemNameAr || it.itemCode
      const label = `${it.itemCode || ''}${it.itemCode ? ' — ' : ''}${name || ''}`.trim()
      return { value: String(it.itemId), label }
    })
  }, [items, language])

  const totalPages = useMemo(
    () => (totalCount ? Math.ceil(totalCount / pageSize) : 0),
    [totalCount, pageSize],
  )

  const loadPage = useCallback(
    async (page: number, sizeOverride?: number) => {
      if (!reportDate) {
        toast.error(t('reports.stockSelectDate'))
        return
      }
      if (isEmptySelection(warehouseId) || isEmptySelection(itemId)) {
        toast.error(t('reports.stockSelectWarehouseItem'))
        return
      }
      // "All" (value 0) is allowed for both warehouse and item.
      const wh = Number(warehouseId)
      const it = Number(itemId)
      if (!Number.isFinite(wh) || !Number.isFinite(it) || wh < 0 || it < 0) {
        toast.error(t('reports.stockSelectWarehouseItem'))
        return
      }

      const size = sizeOverride ?? pageSize
      const reportDateIso = `${reportDate}T23:59:59.000Z`

      setLoading(true)
      try {
        const res = await stockReportService.getStockReport({
          warehouseId: wh,
          itemId: it,
          reportDate: reportDateIso,
          pageNumber: page,
          pageSize: size,
        })
        setRows(res.data)
        setTotalCount(res.totalRecords ?? 0)
        setPageNumber(res.pageNumber ?? page)
        setHasSearched(true)
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } }
        toast.error(err.response?.data?.message || t('reports.stockLoadFailed'))
        setRows([])
        setTotalCount(0)
      } finally {
        setLoading(false)
      }
    },
    [reportDate, warehouseId, itemId, pageSize, t],
  )

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      const blob = [
        r.itemCode,
        r.name,
        r.category,
        r.subCategory,
        r.uom,
        r.type,
        r.warehouse,
        r.batchCode,
        String(r.availableQuantity),
        String(r.damagedStock),
        String(r.expiredStock),
        String(r.holdStock),
        String(r.totalQuantity),
        String(r.unitSalesPrice),
        r.expiryDate,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return blob.includes(q)
    })
  }, [rows, searchQuery])

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.available += r.availableQuantity || 0
        acc.damaged += r.damagedStock || 0
        acc.expired += r.expiredStock || 0
        acc.hold += r.holdStock || 0
        acc.total += r.totalQuantity || 0
        return acc
      },
      { available: 0, damaged: 0, expired: 0, hold: 0, total: 0 },
    )
  }, [filteredRows])

  const columnMenu = useMemo(
    () => [
      { id: 'itemCode', label: t('reports.itemCode') },
      { id: 'name', label: t('reports.itemName') },
      { id: 'category', label: t('reports.category') },
      { id: 'subCategory', label: t('reports.subCategory') },
      { id: 'uom', label: t('reports.uom') },
      { id: 'type', label: t('reports.type') },
      { id: 'warehouse', label: t('reports.warehouse') },
      { id: 'batchCode', label: t('reports.batchCode') },
      { id: 'availableQuantity', label: t('reports.stockAvailableQty') },
      { id: 'damagedStock', label: t('reports.stockDamagedStock') },
      { id: 'expiredStock', label: t('reports.stockExpiredStock') },
      { id: 'holdStock', label: t('reports.stockHoldStock') },
      { id: 'totalQuantity', label: t('reports.stockTotalQty') },
      { id: 'unitSalesPrice', label: t('reports.stockUnitSalePrice') },
      { id: 'expiryDate', label: t('reports.stockExpiryDate') },
    ],
    [t],
  )

  const toggleCol = (id: string) => {
    setColVis((p) => ({ ...p, [id]: !p[id] }))
  }

  const handlePageSizeChange = (v: string) => {
    const n = Number(v)
    setPageSize(n)
    setPageNumber(1)
    if (hasSearched) void loadPage(1, n)
  }

  const handleSearchClick = () => {
    void loadPage(1)
  }

  const handlePrint = () => {
    if (!selectedBranchId) {
      toast.error(t('unsellableInventory.selectBranchForWarehouses'))
      return
    }
    if (!reportDate || isEmptySelection(warehouseId) || isEmptySelection(itemId)) {
      toast.error(t('reports.stockPrintNeedFilters'))
      return
    }
    const wh = warehouses.find((w) => String(w.id) === warehouseId)
    const whLabel = isAllSelection(warehouseId)
      ? t('common.all')
      : wh
        ? [wh.code?.trim(), wh.name?.trim()].filter(Boolean).join(' — ') || String(wh.id)
        : warehouseId
    const itOpt = itemOptions.find((o) => o.value === itemId)
    const itemLabel = isAllSelection(itemId) ? t('common.all') : (itOpt?.label ?? itemId)
    const params = new URLSearchParams({
      branchId: String(selectedBranchId),
      reportDate,
      warehouseId,
      itemId,
      wh: whLabel,
      item: itemLabel,
    })
    appendSelectedColumnsParam(params, colVis)
    window.open(`/inventory/reports/stock/print?${params.toString()}`, '_blank')
  }

  const handleCopy = async () => {
    if (filteredRows.length === 0) {
      toast.error(t('inventoryDispatch.nothingToCopy'))
      return
    }
    const headers = STOCK_COLUMN_ORDER.filter((id) => colVis[id]).map(
      (id) => columnMenu.find((c) => c.id === id)?.label || id,
    )
    const lines = filteredRows.map((r) => {
      const cells: string[] = []
      if (colVis.itemCode) cells.push(r.itemCode || '')
      if (colVis.name) cells.push(r.name || '')
      if (colVis.category) cells.push(r.category || '')
      if (colVis.subCategory) cells.push(r.subCategory || '')
      if (colVis.uom) cells.push(r.uom || '')
      if (colVis.type) cells.push(r.type || '')
      if (colVis.warehouse) cells.push(r.warehouse || '')
      if (colVis.batchCode) cells.push(r.batchCode || '')
      if (colVis.availableQuantity) cells.push(String(r.availableQuantity ?? ''))
      if (colVis.damagedStock) cells.push(String(r.damagedStock ?? ''))
      if (colVis.expiredStock) cells.push(String(r.expiredStock ?? ''))
      if (colVis.holdStock) cells.push(String(r.holdStock ?? ''))
      if (colVis.totalQuantity) cells.push(String(r.totalQuantity ?? ''))
      if (colVis.unitSalesPrice) cells.push(String(r.unitSalesPrice ?? ''))
      if (colVis.expiryDate) cells.push(formatDisplayDate(r.expiryDate))
      return cells.join('\t')
    })
    const text = [headers.join('\t'), ...lines].join('\n')
    try {
      await navigator.clipboard.writeText(text)
      toast.success(t('inventoryDispatch.copied'))
    } catch {
      toast.error(t('reports.stockCopyFailed'))
    }
  }

  const handleCsv = () => {
    if (filteredRows.length === 0) {
      toast.error(t('inventoryDispatch.nothingToExport'))
      return
    }
    const headers = STOCK_COLUMN_ORDER.filter((id) => colVis[id]).map(
      (id) => columnMenu.find((c) => c.id === id)?.label || id,
    )
    const csvRows = filteredRows.map((r) => {
      const cells: string[] = []
      if (colVis.itemCode) cells.push(escapeCsvCell(r.itemCode || ''))
      if (colVis.name) cells.push(escapeCsvCell(r.name || ''))
      if (colVis.category) cells.push(escapeCsvCell(r.category || ''))
      if (colVis.subCategory) cells.push(escapeCsvCell(r.subCategory || ''))
      if (colVis.uom) cells.push(escapeCsvCell(r.uom || ''))
      if (colVis.type) cells.push(escapeCsvCell(r.type || ''))
      if (colVis.warehouse) cells.push(escapeCsvCell(r.warehouse || ''))
      if (colVis.batchCode) cells.push(escapeCsvCell(r.batchCode || ''))
      if (colVis.availableQuantity) cells.push(escapeCsvCell(String(r.availableQuantity ?? '')))
      if (colVis.damagedStock) cells.push(escapeCsvCell(String(r.damagedStock ?? '')))
      if (colVis.expiredStock) cells.push(escapeCsvCell(String(r.expiredStock ?? '')))
      if (colVis.holdStock) cells.push(escapeCsvCell(String(r.holdStock ?? '')))
      if (colVis.totalQuantity) cells.push(escapeCsvCell(String(r.totalQuantity ?? '')))
      if (colVis.unitSalesPrice) cells.push(escapeCsvCell(String(r.unitSalesPrice ?? '')))
      if (colVis.expiryDate) cells.push(escapeCsvCell(formatDisplayDate(r.expiryDate)))
      return cells.join(',')
    })
    const headerLine = headers.map(escapeCsvCell).join(',')
    const blob = new Blob([[headerLine, ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    const url = URL.createObjectURL(blob)
    a.href = url
    a.download = `stock-report_${reportDate}_${warehouseId}_${itemId}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success(t('inventoryDispatch.exported'))
  }

  const tailColumnIds = STOCK_COLUMN_ORDER.slice(8)
  const anyTailColumnVisible = tailColumnIds.some((id) => colVis[id])
  const labelColSpan = !anyTailColumnVisible
    ? Math.max(1, countVisibleInRange(colVis, STOCK_COLUMN_ORDER, 0, STOCK_COLUMN_ORDER.length))
    : Math.max(1, countVisibleInRange(colVis, STOCK_COLUMN_ORDER, 0, 8))

  const showEmptyTable = hasSearched && !loading && filteredRows.length === 0

  return (
    <div className="stock-report-root space-y-6 p-6">
      <style>{`
        @media print {
          .stock-report-root .no-print { display: none !important; }
          .stock-report-root { padding: 1rem !important; }
        }
      `}</style>

      <div className="no-print">
        <PageHeader title={t('navigation.reports')} description={t('navigation.stockReport')} />
      </div>

      <Card className="no-print">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('reports.date')}</Label>
              <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>
                {t('unsellableInventory.warehouse')} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={warehouseId}
                onValueChange={(v) => {
                  setWarehouseId(v)
                  // When switching warehouse, reset item to "All" if user picked "All"
                  // warehouse, otherwise back to empty placeholder so they re-pick.
                  setItemId(isAllSelection(v) ? SELECT_ALL : SELECT_EMPTY)
                }}
                disabled={!selectedBranchId || warehousesLoading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent searchable>
                  <SelectItem value={SELECT_EMPTY}>
                    {!selectedBranchId
                      ? t('unsellableInventory.selectBranchForWarehouses')
                      : warehousesLoading
                        ? t('common.loading', { defaultValue: 'Loading…' })
                        : t('unsellableInventory.selectWarehouse')}
                  </SelectItem>
                  <SelectItem value={SELECT_ALL}>{t('common.all')}</SelectItem>
                  {warehouses.map((w) => {
                    const c = w.code?.trim()
                    const n = w.name?.trim()
                    const label = c && n ? `${c} — ${n}` : n || c || String(w.id)
                    return (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {label}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                {t('unsellableInventory.item')} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={itemId}
                onValueChange={setItemId}
                disabled={isEmptySelection(warehouseId) || itemsLoading}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent searchable>
                  <SelectItem value={SELECT_EMPTY}>
                    {isEmptySelection(warehouseId)
                      ? t('unsellableInventory.selectWarehouseForItems')
                      : itemsLoading
                        ? t('common.loading', { defaultValue: 'Loading…' })
                        : t('unsellableInventory.selectItem')}
                  </SelectItem>
                  <SelectItem value={SELECT_ALL}>{t('common.all')}</SelectItem>
                  {itemOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label>{t('reports.search')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('reports.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
              <Button onClick={handleSearchClick} disabled={loading}>
                {loading ? t('reports.loading') : t('reports.search')}
              </Button>
              <TableColumnVisibilityMenu columns={columnMenu} visibility={colVis} onToggle={toggleCol} />
              <Button variant="outline" size="sm" onClick={() => void handleCopy()} disabled={filteredRows.length === 0}>
                <Copy className="h-4 w-4 mr-2" />
                {t('reports.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv} disabled={filteredRows.length === 0}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('reports.download')}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                {t('reports.print')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="no-print border-b px-4 py-2 text-sm text-muted-foreground">
            {t('navigation.reports')} — {t('navigation.stockReport')}
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <p className="mt-4 text-muted-foreground">{t('reports.loading')}</p>
            </div>
          ) : !hasSearched ? (
            <div className="p-12">
              <EmptyState title={t('navigation.stockReport')} description={t('reports.stockClickSearch')} />
            </div>
          ) : showEmptyTable ? (
            <div className="p-12">
              <EmptyState title={t('reports.stockNoData')} description={t('reports.stockEmptyResult')} />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      {colVis.itemCode && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.itemCode')}
                        </TableHead>
                      )}
                      {colVis.name && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.itemName')}
                        </TableHead>
                      )}
                      {colVis.category && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.category')}
                        </TableHead>
                      )}
                      {colVis.subCategory && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.subCategory')}
                        </TableHead>
                      )}
                      {colVis.uom && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.uom')}
                        </TableHead>
                      )}
                      {colVis.type && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.type')}
                        </TableHead>
                      )}
                      {colVis.warehouse && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.warehouse')}
                        </TableHead>
                      )}
                      {colVis.batchCode && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.batchCode')}
                        </TableHead>
                      )}
                      {colVis.availableQuantity && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.stockAvailableQty')}
                        </TableHead>
                      )}
                      {colVis.damagedStock && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.stockDamagedStock')}
                        </TableHead>
                      )}
                      {colVis.expiredStock && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.stockExpiredStock')}
                        </TableHead>
                      )}
                      {colVis.holdStock && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.stockHoldStock')}
                        </TableHead>
                      )}
                      {colVis.totalQuantity && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.stockTotalQty')}
                        </TableHead>
                      )}
                      {colVis.unitSalesPrice && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.stockUnitSalePrice')}
                        </TableHead>
                      )}
                      {colVis.expiryDate && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10">
                          {t('reports.stockExpiryDate')}
                        </TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.map((r, idx) => (
                      <TableRow key={`${r.itemCode}-${r.batchCode}-${idx}`}>
                        {colVis.itemCode && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.itemCode || '-'}</TableCell>
                        )}
                        {colVis.name && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.name || '-'}</TableCell>
                        )}
                        {colVis.category && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.category || '-'}</TableCell>
                        )}
                        {colVis.subCategory && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.subCategory || '-'}</TableCell>
                        )}
                        {colVis.uom && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.uom || '-'}</TableCell>
                        )}
                        {colVis.type && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.type || '-'}</TableCell>
                        )}
                        {colVis.warehouse && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.warehouse?.trim() || '-'}</TableCell>
                        )}
                        {colVis.batchCode && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.batchCode || '-'}</TableCell>
                        )}
                        {colVis.availableQuantity && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(r.availableQuantity)}</TableCell>
                        )}
                        {colVis.damagedStock && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(r.damagedStock)}</TableCell>
                        )}
                        {colVis.expiredStock && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(r.expiredStock)}</TableCell>
                        )}
                        {colVis.holdStock && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(r.holdStock)}</TableCell>
                        )}
                        {colVis.totalQuantity && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(r.totalQuantity)}</TableCell>
                        )}
                        {colVis.unitSalesPrice && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatMoney(r.unitSalesPrice)}</TableCell>
                        )}
                        {colVis.expiryDate && (
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDisplayDate(r.expiryDate)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2 border-primary/20 bg-primary/5 font-bold">
                      <TableCell colSpan={labelColSpan} className="px-4 py-2 text-sm text-center align-middle">
                        {t('reports.total')}
                      </TableCell>
                      {colVis.availableQuantity && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(totals.available)}</TableCell>
                      )}
                      {colVis.damagedStock && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(totals.damaged)}</TableCell>
                      )}
                      {colVis.expiredStock && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(totals.expired)}</TableCell>
                      )}
                      {colVis.holdStock && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(totals.hold)}</TableCell>
                      )}
                      {colVis.totalQuantity && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatQty(totals.total)}</TableCell>
                      )}
                      {colVis.unitSalesPrice && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">—</TableCell>
                      )}
                      {colVis.expiryDate && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">—</TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="no-print border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  {t('reports.stockShowing', {
                    start: totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                    end: totalCount === 0 ? 0 : Math.min(pageNumber * pageSize, totalCount),
                    total: totalCount,
                  })}
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadPage(Math.max(pageNumber - 1, 1))}
                    disabled={pageNumber <= 1 || loading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap px-2">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void loadPage(Math.min(pageNumber + 1, totalPages || 1))}
                    disabled={pageNumber >= totalPages || loading || totalPages === 0}
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
